import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckBadgeIcon } from '@heroicons/react/24/outline';
import StaffShell from '../StaffShell';
import { inputClass, labelClass } from '../AuthShell';
import staffApi, { apiError } from '../api';
import { useAuth } from '../AuthContext';
import { WORK_LABELS, PENDING_REASON_LABELS } from '../constants';
import StatusBadge from '../components/StatusBadge';
import Lightbox from '../components/Lightbox';

function Row({ label, children }) {
  if (children === null || children === undefined || children === '') return null;
  return (
    <div>
      <div className="text-ink-soft text-xs font-semibold uppercase tracking-wide">{label}</div>
      <div className="text-ink">{children}</div>
    </div>
  );
}

export default function ReportDetail() {
  const { id } = useParams();
  const { isOffice } = useAuth();
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [review, setReview] = useState({ follow_up_date: '', inventory_updated: false });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const backTo = isOffice ? '/staff/app/office/reports' : '/staff/app/reports';

  const load = () => {
    staffApi.get(`/reports/${id}/`)
      .then((r) => {
        setReport(r.data);
        setReview({
          follow_up_date: r.data.follow_up_date || '',
          inventory_updated: !!r.data.inventory_updated,
        });
      })
      .catch((err) => setError(apiError(err)));
  };

  useEffect(load, [id]);

  const saveReview = async () => {
    setSaving(true);
    setError('');
    try {
      await staffApi.patch(`/reports/${id}/review/`, {
        follow_up_date: review.follow_up_date || null,
        inventory_updated: review.inventory_updated,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      load();
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (error && !report) {
    return <StaffShell backTo={backTo}><p className="text-red-300">{error}</p></StaffShell>;
  }
  if (!report) {
    return <StaffShell backTo={backTo}><p className="text-white/70">Loading…</p></StaffShell>;
  }

  const materials = report.materials_used || [];
  const photos = report.photos || [];
  const team = report.team_member_details || [];

  return (
    <StaffShell backTo={backTo}>
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="text-white text-2xl font-extrabold">
            {report.customer_name || WORK_LABELS[report.work_type] || 'Job report'}
          </h1>
          <p className="text-white/60 text-sm mt-1">
            {report.date} · {report.technician_name || report.technician_username}
          </p>
        </div>
        <StatusBadge status={report.status} />
      </div>

      <div className="space-y-5">
        <section className="rounded-2xl border border-line bg-surface p-5 grid sm:grid-cols-2 gap-4">
          <Row label="Work type">{WORK_LABELS[report.work_type] || report.work_type}</Row>
          <Row label="Time">
            {report.start_time || report.end_time ? `${report.start_time || '—'} – ${report.end_time || '—'}` : null}
          </Row>
          <Row label="Phone">{report.customer_phone}</Row>
          <Row label="Location">{report.customer_location}</Row>
          <Row label="Unit">{report.unit_number}</Row>
          {report.is_team && <Row label="Team">{team.length ? team.map((t) => t.name).join(', ') : 'Team job'}</Row>}
          {report.pending_reason && (
            <Row label="Pending reason">{PENDING_REASON_LABELS[report.pending_reason] || report.pending_reason}</Row>
          )}
        </section>

        {report.work_performed && (
          <section className="rounded-2xl border border-line bg-surface p-5">
            <div className="text-ink-soft text-xs font-semibold uppercase tracking-wide mb-1">Work performed</div>
            <p className="text-ink whitespace-pre-line">{report.work_performed}</p>
          </section>
        )}

        <section className="rounded-2xl border border-line bg-surface p-5">
          <div className="text-ink-soft text-xs font-semibold uppercase tracking-wide mb-2">Materials used</div>
          {materials.length === 0 ? (
            <p className="text-ink-soft text-sm">None recorded.</p>
          ) : (
            <ul className="divide-y divide-line">
              {materials.map((m) => (
                <li key={m.id} className="flex justify-between py-2 text-ink">
                  <span>{m.material_name}</span>
                  <span className="font-semibold">{m.quantity} <span className="text-ink-soft text-sm">{m.unit}</span></span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {photos.length > 0 && (
          <section className="rounded-2xl border border-line bg-surface p-5">
            <div className="text-ink-soft text-xs font-semibold uppercase tracking-wide mb-2">Proof of work</div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {photos.map((p) => (
                <button key={p.id} type="button" onClick={() => setLightbox(p.image)}
                  className="block focus:outline-none">
                  <img src={p.image} alt="proof" className="h-20 w-full object-cover rounded-lg border border-line hover:opacity-90 transition" />
                </button>
              ))}
            </div>
          </section>
        )}

        {report.remarks && (
          <section className="rounded-2xl border border-line bg-surface p-5">
            <div className="text-ink-soft text-xs font-semibold uppercase tracking-wide mb-1">Remarks</div>
            <p className="text-ink whitespace-pre-line">{report.remarks}</p>
          </section>
        )}

        {isOffice ? (
          <section className="rounded-2xl border border-lime/40 bg-surface p-5">
            <div className="flex items-center gap-2 mb-4">
              <CheckBadgeIcon className="h-5 w-5 text-lime" />
              <h2 className="text-ink font-bold">Office review</h2>
            </div>
            {report.reviewed_by_username && (
              <p className="text-lime text-sm font-semibold mb-4">Reviewed by {report.reviewed_by_username}</p>
            )}
            {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Follow-up date</label>
                <input type="date" value={review.follow_up_date}
                  onChange={(e) => setReview((r) => ({ ...r, follow_up_date: e.target.value }))} className={inputClass} />
              </div>
              <label className="flex items-center gap-2 text-ink font-semibold sm:mt-7">
                <input type="checkbox" checked={review.inventory_updated}
                  onChange={(e) => setReview((r) => ({ ...r, inventory_updated: e.target.checked }))}
                  className="h-4 w-4 accent-lime" />
                Inventory updated
              </label>
            </div>
            <button onClick={saveReview} disabled={saving}
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-lime text-navy font-bold px-5 py-2.5 hover:bg-green-400 transition disabled:opacity-60">
              {saving ? 'Saving…' : saved ? 'Saved ✓' : report.reviewed_by_username ? 'Update review' : 'Mark as reviewed'}
            </button>
          </section>
        ) : (
          report.reviewed_by_username && (
            <p className="text-lime text-sm font-semibold">Reviewed by {report.reviewed_by_username}</p>
          )
        )}
      </div>

      <Lightbox src={lightbox} onClose={() => setLightbox(null)} />
    </StaffShell>
  );
}
